import React, { useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Password input with a show/hide toggle. Drop-in replacement for `<Input type="password" />`.
 *
 * Usage:
 *   <PasswordInput value={pw} onChange={(e) => setPw(e.target.value)} data-testid="signin-password" />
 *
 * Props: everything <Input /> accepts. Ref is forwarded to the underlying input so `focus()` works.
 */
export const PasswordInput = forwardRef(function PasswordInput(
  { className = "", "data-testid": dataTestId, ...rest },
  ref,
) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        ref={ref}
        type={visible ? "text" : "password"}
        autoComplete={rest.autoComplete || "current-password"}
        className={`pr-10 ${className}`}
        data-testid={dataTestId}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-summit-primary-soft/60 hover:text-summit-primary hover:bg-summit-glacier transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-summit-primary"
        data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
      >
        {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
});

export default PasswordInput;
